import { z } from "zod"
export { webpayValidationCollectionSchema, webpayValidationCartSchema } from "./webpay-validation"
export * from "./credit"
export * from "./installation"
export * from "./search"
export * from "./catalog"

const colorSchema = z.string().regex(/^#[\da-f]{6}$/i)
const cssLengthSchema = z.string().regex(/^\d+(?:\.\d+)?(?:px|rem)$/)
const environmentNameSchema = z.string().regex(/^[A-Z][A-Z0-9_]+$/)

export const storefrontDefinitionSchema = z.object({
  id: z
    .string()
    .min(1)
    .max(32)
    .regex(/^[a-z][a-z0-9-]*$/),
  origin: z.url(),
  locale: z.literal("es-CL"),
  currencyCode: z.literal("clp"),
  brand: z.object({
    name: z.string().min(1),
    shortName: z.string().min(1),
    logoPath: z.string().startsWith("/"),
    logoWidth: z.number().int().positive().optional(),
    logoHeight: z.number().int().positive().optional(),
    iconPath: z.string().startsWith("/").optional(),
  }),
  business: z
    .object({
      legalName: z.string().min(1),
      taxId: z.string().min(1),
      address: z.string().min(1),
      email: z.email(),
      website: z.url(),
    })
    .optional(),
  header: z.object({
    logoDisplay: z.enum(["wordmark", "image"]),
    showAccount: z.boolean(),
    showSearch: z.boolean(),
    sticky: z.boolean(),
    desktopCategoryLimit: z.number().int().min(0).max(8),
  }),
  commerce: z.object({
    mode: z.enum(["commerce", "catalog"]).default("commerce"),
    defaultCountryCode: z.literal("cl"),
    backendUrlEnv: environmentNameSchema,
    publishableKeyEnv: environmentNameSchema,
    salesChannelIdEnv: environmentNameSchema,
    taxPresentation: z.enum(["gross", "net-plus-vat"]),
  }),
  search: z.object({
    tenant: z.string().min(1),
    gatewayUrlEnv: environmentNameSchema,
  }),
  cache: z.object({
    namespace: z
      .string()
      .min(1)
      .regex(/^[a-z][a-z0-9-]*$/),
  }),
  content: z
    .object({
      eyebrow: z.string(),
      title: z.string(),
      introduction: z.string(),
      heroImage: z.string().startsWith("/"),
      featuredCategory: z.string().optional(),
      categories: z.array(
        z.object({
          title: z.string(),
          handle: z.string(),
          description: z.string(),
          productHandle: z.string(),
          imageUrl: z.string().url().optional(),
        })
      ),
      steps: z.array(z.object({ title: z.string(), description: z.string() })),
      faqs: z.array(z.object({ question: z.string(), answer: z.string() })),
    })
    .optional(),
  theme: z.object({
    colorBrand: colorSchema,
    colorBrandHover: colorSchema,
    colorOnBrand: colorSchema,
    colorAccent: colorSchema,
    colorBackground: colorSchema,
    colorSurface: colorSchema,
    colorForeground: colorSchema,
    colorMuted: colorSchema,
    colorBorder: colorSchema,
    colorFocus: colorSchema,
    colorHeaderBackground: colorSchema,
    colorHeaderForeground: colorSchema,
    colorHeaderBorder: colorSchema,
    colorHeaderInteractive: colorSchema,
    colorHeaderBadgeBackground: colorSchema,
    colorHeaderBadgeForeground: colorSchema,
    fontSans: z.string().min(1),
    fontHeading: z.string().min(1),
    headerHeight: cssLengthSchema,
    headerLogoMaxWidth: cssLengthSchema,
    radiusControl: cssLengthSchema,
    radiusCard: cssLengthSchema,
    shadowCard: z.string().min(1),
  }),
  features: z.object({
    installationBooking: z.boolean().default(false),
    creditLine: z.boolean().default(false),
    blog: z.boolean(),
    renting: z.boolean(),
    quotes: z.boolean(),
    reviews: z.boolean(),
    wishlist: z.boolean(),
    receptionist: z.boolean(),
  }),
})

export type StorefrontDefinition = z.infer<typeof storefrontDefinitionSchema>

export function defineStorefront(
  input: z.input<typeof storefrontDefinitionSchema>
): StorefrontDefinition {
  return storefrontDefinitionSchema.parse(input)
}

const runtimeEnvironmentSchema = z.object({
  mode: z.enum(["commerce", "catalog"]).default("commerce"),
  backendUrl: z.string().min(1),
  publishableKey: z.string().min(1).optional(),
  salesChannelId: z.string().startsWith("sc_"),
  searchGatewayUrl: z.url().optional(),
}).superRefine((value, context) => {
  if (value.mode === "commerce") {
    for (const field of ["publishableKey", "searchGatewayUrl"] as const) {
      if (!value[field]) context.addIssue({ code: "custom", path: [field], message: "Required for commerce" })
    }
  }
})

export type RuntimeEnvironment = z.infer<typeof runtimeEnvironmentSchema>

function normalizedUrl(value: string): string {
  const withoutWhitespace = value
    .replace(/[\r\n\t]/g, "")
    .trim()
    .replace(/\/+$/, "")
  return new URL(withoutWhitespace).toString().replace(/\/$/, "")
}

export function defineRuntimeEnvironment(
  input: z.input<typeof runtimeEnvironmentSchema>
): RuntimeEnvironment {
  const parsed = runtimeEnvironmentSchema.parse(input)
  return {
    ...parsed,
    backendUrl: normalizedUrl(parsed.backendUrl),
    searchGatewayUrl: parsed.searchGatewayUrl ? normalizedUrl(parsed.searchGatewayUrl) : undefined,
  }
}
